/**
 * Truck cover page content block (sections/truck-cover-page-content.liquid).
 *
 * - Model pages (where Google Shopping lands): when the selector holds a year of
 *   this model, swap in that year's version of the block, pre-rendered in a
 *   <template data-tpc-year>, and the years nav from <template data-tpc-nav>. Back to
 *   the model version when the year is cleared. Driven by the truck-fitment:truck
 *   event from assets/truck-fitment.js, so the block and the selector never disagree.
 * - Make pages: once the selector holds a model of this make, the "Pick your model"
 *   card stops asking and marks that model instead.
 * - "Pick your bed length" hides once the selector shows a fitted cover
 *   (truck-fitment:cover for the selected bed).
 * - Phones: fitment notes fold behind a toggle; generations become tabs.
 * - Photos: the entry's gallery, topped up to data-max with review photos of this
 *   model. Only photos the API matched to the model -- never its make-wide or
 *   all-reviews fallback, which would put a Ram under "On F-150s".
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function same(a, b) {
    return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function activateGen(gens, index) {
    gens.querySelectorAll('[data-tpc-gen]').forEach(function (group) {
      group.classList.toggle('is-active', group.getAttribute('data-tpc-gen') === String(index));
    });
    gens.querySelectorAll('[data-tpc-gen-tab]').forEach(function (tab) {
      tab.setAttribute('aria-pressed', tab.getAttribute('data-tpc-gen-tab') === String(index) ? 'true' : 'false');
    });
  }

  //Open the generation holding the current year, else the newest.
  function initGens(scope) {
    scope.querySelectorAll('[data-tpc-gens]').forEach(function (gens) {
      if (!gens.querySelector('[data-tpc-gen-tab]')) return;
      gens.classList.add('is-tabbed');
      var current = gens.querySelector('.tpc-year[aria-current]');
      var group = current ? current.closest('[data-tpc-gen]') : gens.querySelector('[data-tpc-gen]');
      activateGen(gens, group ? group.getAttribute('data-tpc-gen') : 0);
    });
  }

  function initBlock(root) {
    var level = root.getAttribute('data-page-level');
    var top = root.querySelector('[data-tpc-top]');
    var navSlot = root.querySelector('[data-tpc-nav-slot]');
    var modelTop = top ? top.innerHTML : '';
    var shownYear = '';
    var selection = {};
    var cover = {};

    initGens(root);

    root.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-tpc-notes-toggle]');
      if (toggle) {
        var notes = toggle.closest('[data-tpc-notes]');
        var open = !notes.classList.contains('is-open');
        notes.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        return;
      }
      var tab = event.target.closest('[data-tpc-gen-tab]');
      if (tab) {
        activateGen(tab.closest('[data-tpc-gens]'), tab.getAttribute('data-tpc-gen-tab'));
        return;
      }
      //"Different truck?": open the selector fresh if it is showing a result.
      if (event.target.closest('[data-tpc-change-truck]')) {
        var change = document.getElementById('change-vehicle');
        if (change && change.offsetParent !== null) change.click();
        return;
      }
      var photo = event.target.closest('button[data-tpc-photo]');
      if (photo) {
        var full = photo.getAttribute('data-full');
        var img = photo.querySelector('img');
        if (window.PeragonReviewsLightbox) window.PeragonReviewsLightbox(full, img ? img.alt : 'Customer photo');
        else window.open(full, '_blank', 'noopener');
      }
    });

    function showYear(year) {
      if (level !== 'model' || !top) return;
      var template = year ? root.querySelector('template[data-tpc-year="' + String(year).replace(/[^0-9]/g, '') + '"]') : null;
      var wanted = template ? String(year) : '';
      if (wanted === shownYear) return;
      shownYear = wanted;

      top.innerHTML = template ? template.innerHTML : modelTop;
      initGens(top);

      if (!navSlot) return;
      navSlot.innerHTML = '';
      var navTemplate = template && root.querySelector('template[data-tpc-nav]');
      if (!navTemplate) return;
      var nav = navTemplate.content.cloneNode(true);
      nav.querySelectorAll('[data-tpc-year-link="' + wanted + '"]').forEach(function (link) {
        link.classList.add('is-current');
        link.setAttribute('aria-current', 'page');
      });
      navSlot.appendChild(nav);
      initGens(navSlot);
    }

    //Make page: the selector already holds the model, so don't ask for it again.
    function showModel(model) {
      if (level !== 'make' || !top) return;
      var marked = false;
      top.querySelectorAll('[data-tpc-model]').forEach(function (link) {
        var current = !!model && same(link.getAttribute('data-tpc-model'), model);
        marked = marked || current;
        if (current) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      top.querySelectorAll('[data-tpc-ask]').forEach(function (el) { el.hidden = marked; });
      top.querySelectorAll('[data-tpc-picked]').forEach(function (el) { el.hidden = !marked; });
    }

    function update() {
      var mine = same(selection.make, root.getAttribute('data-make')) &&
        (!root.getAttribute('data-model') || same(selection.model, root.getAttribute('data-model')));
      showYear(mine ? selection.year : '');
      showModel(mine ? selection.model : '');
      root.classList.toggle('has-fit', !!(selection.bed && cover.fits && String(cover.truckId) === String(selection.bed)));
    }

    document.addEventListener('truck-fitment:truck', function (event) {
      selection = event.detail || {};
      update();
    });
    document.addEventListener('truck-fitment:cover', function (event) {
      cover = event.detail || {};
      update();
    });
    //truck-fitment.js may have announced a saved truck before this script ran.
    var state = window.PeragonTruckFitment;
    if (state && (state.truck || state.cover)) {
      selection = state.truck || {};
      cover = state.cover || {};
      update();
    }

    initPhotos(root.querySelector('[data-tpc-photos]'));
  }

  function initPhotos(box) {
    if (!box) return;
    var grid = box.querySelector('[data-tpc-photos-grid]');
    var max = Number(box.getAttribute('data-max')) || 4;
    var need = max - (grid ? grid.children.length : 0);
    var make = (box.getAttribute('data-make') || '').trim();
    if (!grid || need <= 0 || !make) return;

    var model = (box.getAttribute('data-model') || '').trim();
    var year = (box.getAttribute('data-year') || '').trim();
    var url = (box.getAttribute('data-api-url') || 'https://admin.peragon.com/shopify/reviews') +
      '?page=1&limit=20&with_photos=1&min=1' +
      '&reporting_category_id=' + encodeURIComponent(box.getAttribute('data-reporting-category-id') || '1') +
      '&make=' + encodeURIComponent(make) + (model ? '&model=' + encodeURIComponent(model) : '');

    fetch(url).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.json();
    }).then(function (payload) {
      if (!payload || payload.scope !== (model ? 'model' : 'make')) return;
      var reviews = (payload.reviews || []).slice();
      //This page's own year first ("2017 Ford F-150").
      if (year) {
        reviews.sort(function (a, b) {
          return (String(b.truck || '').indexOf(year) === 0) - (String(a.truck || '').indexOf(year) === 0);
        });
      }
      var tiles = [];
      reviews.forEach(function (review) {
        (review.photos || []).forEach(function (photo) {
          if (tiles.length >= need || !photo || !(photo.full || photo.thumb)) return;
          var alt = (review.truck ? review.truck + ' ' : '') + 'owner photo';
          tiles.push(
            '<button type="button" class="tpc-photo" data-tpc-photo data-full="' + escapeHtml(photo.full || photo.thumb) + '" aria-label="' + escapeHtml(alt) + '">' +
              '<img src="' + escapeHtml(photo.full || photo.thumb) + '" alt="' + escapeHtml(alt) + '" loading="lazy">' +
            '</button>'
          );
        });
      });
      if (!tiles.length) return;
      grid.insertAdjacentHTML('beforeend', tiles.join(''));
      box.hidden = false;
    }).catch(function (err) {
      console.warn('[truck-page-content] review photos unavailable.', err);
    });
  }

  ready(function () {
    document.querySelectorAll('[data-truck-page-content]').forEach(initBlock);
  });
})();
